import React from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import { X } from 'lucide-react';

export interface CompanyUserRow {
    id: number;
    name: string | null;
    email: string | null;
    role: string;
    status: string;
    isActive: boolean;
}

interface CompanyUsersPanelProps {
    users: CompanyUserRow[];
    isLoading: boolean;
    onClose: () => void;
}

const getStatusColor = (row: CompanyUserRow): string =>
    !row.isActive ? 'var(--color-negative-amount)' : row.status === 'active' ? 'var(--color-primary)' : 'var(--color-text-muted)';

const getStatusLabel = (row: CompanyUserRow): string =>
    !row.isActive ? 'Removed' : (row.status.charAt(0).toUpperCase() + row.status.slice(1));

// Right-side panel shown beside the Company Details modal, listing that
// entity's EntityUsers (same data as Settings > Security's user list, just
// read-only here) - same layout approach as ProvisionOccurrencesPanel next
// to Edit Provision: a plain flex list rather than MaterialReactTable, sized
// for a ~280px sidebar rather than a full table.
const CompanyUsersPanel: React.FC<CompanyUsersPanelProps> = ({ users, isLoading, onClose }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0 }}>
        <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1,
            flexShrink: 0, background: 'var(--color-background, #fff)',
        }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Users</Typography>
            <IconButton size="small" onClick={onClose} aria-label="Hide users">
                <X size={16} />
            </IconButton>
        </Box>
        {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={24} />
            </Box>
        ) : users.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', py: 4, px: 1.5 }}>
                No users yet
            </Typography>
        ) : (
            <>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderTop: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    background: 'var(--color-container-background)',
                    flexShrink: 0,
                }}>
                    <Typography sx={{ flex: '1 1 0', fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)' }}>User</Typography>
                    <Typography sx={{ flex: '0 0 70px', fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)' }}>Role</Typography>
                    <Typography sx={{ flex: '0 0 60px', fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textAlign: 'right' }}>Status</Typography>
                </Box>
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0, 0, 0, 0.2)', borderRadius: 3 },
                    scrollbarWidth: 'thin',
                }}>
                    {users.map((row) => (
                        <Box
                            key={row.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderBottom: '1px solid #f0f0f0',
                                opacity: row.isActive ? 1 : 0.5,
                            }}
                        >
                            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.name || '(pending invite)'}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.email || '—'}
                                </Typography>
                            </Box>
                            <Typography sx={{ flex: '0 0 70px', fontSize: 12, textTransform: 'capitalize' }}>{row.role}</Typography>
                            <Typography sx={{ flex: '0 0 60px', fontSize: 11, fontWeight: 600, textAlign: 'right', color: getStatusColor(row) }}>
                                {getStatusLabel(row)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </>
        )}
    </Box>
);

export default CompanyUsersPanel;
